import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import AppIcon from '../components/AppIcon';
import TruckLoader from '../components/TruckLoader';
import './SolShell.css';

const SourceConfig = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, isAdmin, canUpload, canEditSourceConfig, getIdToken } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [authType, setAuthType] = useState('api_key');
  const [baselineAuthType, setBaselineAuthType] = useState('api_key');
  const [saveError, setSaveError] = useState('');
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lastTestedPayload, setLastTestedPayload] = useState(null);
  const [editingHasCredentials, setEditingHasCredentials] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authProperties, setAuthProperties] = useState([{ key: '', value: '', secret: false }]);
  const [queryParamRows, setQueryParamRows] = useState([{ key: '', value: '' }]);
  const [baselineCredentialState, setBaselineCredentialState] = useState({ properties: {}, secrets: [] });
  const [form, setForm] = useState({
    sourceName: '',
    sourceDesc: '',
    sourceType: 'api',
    baseUrl: '',
    endpoint: '',
    syncInterval: '300',
  });

  const canManageSources = Boolean(isAdmin || canEditSourceConfig);
  const ACCESS_DENIED_MESSAGE = "You don't have access, please request access.";

  const defaultAuthPropertiesFor = (auth) => {
    if (auth === 'basic') {
      return [
        { key: 'username', value: '', secret: false },
        { key: 'password', value: '', secret: true },
      ];
    }
    if (auth === 'oauth2') {
      return [
        { key: 'client_id', value: '', secret: false },
        { key: 'token_url', value: '', secret: false },
        { key: 'client_secret', value: '', secret: true },
      ];
    }
    if (auth === 'bearer') {
      return [{ key: 'bearer_token', value: '', secret: true }];
    }
    // api_key
    return [
      { key: 'header_name', value: 'X-API-Key', secret: false },
      { key: 'api_key', value: '', secret: true },
    ];
  };

  const rowsToCredentialState = (rows) => {
    const properties = {};
    const secrets = [];
    (rows || []).forEach((r) => {
      const k = (r?.key || '').trim();
      if (!k) return;
      if (r?.secret) secrets.push(k);
      else properties[k] = r?.value ?? '';
    });
    return { properties, secrets };
  };

  const credentialStateToRows = (state) => {
    const props = state?.properties && typeof state.properties === 'object' ? state.properties : {};
    const secs = Array.isArray(state?.secrets) ? state.secrets : [];
    const rows = [];
    Object.entries(props).forEach(([k, v]) => rows.push({ key: String(k), value: v == null ? '' : String(v), secret: false }));
    secs.forEach((k) => rows.push({ key: String(k), value: '', secret: true }));
    return rows.length ? rows : [{ key: '', value: '', secret: false }];
  };

  const queryParamsToRows = (paramsObj) => {
    const obj = paramsObj && typeof paramsObj === 'object' && !Array.isArray(paramsObj) ? paramsObj : {};
    const rows = Object.entries(obj).map(([k, v]) => ({ key: String(k), value: v == null ? '' : String(v) }));
    return rows.length ? rows : [{ key: '', value: '' }];
  };

  const rowsToQueryParams = (rows) => {
    const out = {};
    (rows || []).forEach((r) => {
      const k = (r?.key || '').trim();
      if (!k) return;
      out[k] = r?.value == null ? '' : String(r.value);
    });
    return out;
  };

  const normalizeLoadedCredentials = (creds, fallbackAuth) => {
    const auth = normalizeAuthType(creds?.auth_type || fallbackAuth);

    if (creds && (creds.properties || creds.secrets)) {
      const properties = creds?.properties && typeof creds.properties === 'object' ? creds.properties : {};
      const secretsObj = creds?.secrets && typeof creds.secrets === 'object' ? creds.secrets : {};
      return {
        auth_type: auth,
        properties: Object.fromEntries(Object.entries(properties).map(([k, v]) => [String(k), v == null ? '' : String(v)])),
        secrets: Object.keys(secretsObj).map((k) => String(k)),
      };
    }

    // Legacy payload compatibility (never prefill secret values; only surface keys)
    const properties = {};
    const secrets = [];
    if (auth === 'api_key') {
      properties.header_name = creds?.api_key_header ? String(creds.api_key_header) : 'X-API-Key';
      if (creds?.api_key != null) secrets.push('api_key');
    } else if (auth === 'bearer') {
      if (creds?.bearer_token != null) secrets.push('bearer_token');
    } else if (auth === 'basic') {
      if (creds?.username != null) properties.username = String(creds.username);
      if (creds?.password != null) secrets.push('password');
    } else if (auth === 'oauth2') {
      if (creds?.oauth_client_id != null) properties.client_id = String(creds.oauth_client_id);
      if (creds?.oauth_token_url != null) properties.token_url = String(creds.oauth_token_url);
      if (creds?.oauth_client_secret != null) secrets.push('client_secret');
    }

    // Any legacy extra fields are treated as secrets by default (avoid leaking values)
    const extra = creds?.extra;
    if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
      Object.keys(extra).forEach((k) => {
        const kk = String(k);
        if (!kk) return;
        if (!secrets.includes(kk) && !(kk in properties)) secrets.push(kk);
      });
    }

    return { auth_type: auth, properties, secrets };
  };

  const buildCredentialDelta = () => {
    const currentState = rowsToCredentialState(authProperties);
    const baselineProps = baselineCredentialState?.properties && typeof baselineCredentialState.properties === 'object' ? baselineCredentialState.properties : {};
    const baselineSecrets = new Set(Array.isArray(baselineCredentialState?.secrets) ? baselineCredentialState.secrets : []);

    const currentProps = currentState.properties || {};
    const currentSecretKeys = new Set(currentState.secrets || []);
    const currentSecretValues = {};
    (authProperties || []).forEach((r) => {
      const k = (r?.key || '').trim();
      if (!k) return;
      if (!r?.secret) return;
      const v = r?.value == null ? '' : String(r.value);
      if (v !== '') currentSecretValues[k] = v;
    });

    const remove_properties = Object.keys(baselineProps).filter((k) => !(k in currentProps) && !currentSecretKeys.has(k));
    const remove_secrets = Array.from(baselineSecrets).filter((k) => !currentSecretKeys.has(k) && !(k in currentProps));

    const properties = {};
    Object.entries(currentProps).forEach(([k, v]) => {
      const vv = v == null ? '' : String(v);
      if (!(k in baselineProps) || String(baselineProps[k]) !== vv) properties[k] = vv;
    });

    const secrets = currentSecretValues;
    const hasChanges =
      remove_properties.length > 0 ||
      remove_secrets.length > 0 ||
      Object.keys(properties).length > 0 ||
      Object.keys(secrets).length > 0;

    if (!hasChanges) return null;
    return {
      auth_type: authType,
      properties,
      secrets,
      remove_properties,
      remove_secrets,
    };
  };

  const buildCreateCredentials = () => {
    const state = rowsToCredentialState(authProperties);
    const properties = {};
    Object.entries(state.properties || {}).forEach(([k, v]) => {
      properties[String(k)] = v == null ? '' : String(v);
    });
    const secrets = {};
    (authProperties || []).forEach((r) => {
      const k = (r?.key || '').trim();
      if (!k || !r?.secret) return;
      const v = r?.value == null ? '' : String(r.value);
      if (v !== '') secrets[k] = v;
    });
    return { auth_type: authType, properties, secrets };
  };

  const requiredFieldsForAuth = (auth) => {
    if (auth === 'basic') return { properties: ['username'], secrets: ['password'] };
    if (auth === 'oauth2') return { properties: ['client_id', 'token_url'], secrets: ['client_secret'] };
    if (auth === 'bearer') return { properties: [], secrets: ['bearer_token'] };
    return { properties: ['header_name'], secrets: ['api_key'] };
  };

  const validateCredentialsForTestOrCreate = (credentials) => {
    const req = requiredFieldsForAuth(authType);
    const props = credentials?.properties || {};
    const secs = credentials?.secrets || {};

    const missing = [];
    req.properties.forEach((k) => {
      const v = props[k];
      if (String(v ?? '').trim() === '') missing.push(`property '${k}'`);
    });
    req.secrets.forEach((k) => {
      const v = secs[k];
      if (String(v ?? '') === '') missing.push(`secret '${k}'`);
    });
    return missing;
  };

  const displayName = useMemo(() => {
    return user?.displayName || user?.email || 'User';
  }, [user]);

  const avatar = useMemo(() => {
    const basis = (user?.displayName || user?.email || 'U').trim();
    const parts = basis.split(/\s+/).filter(Boolean);
    const initials = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : `${basis[0]}`;
    return initials.toUpperCase();
  }, [user]);

  const [sources, setSources] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityError, setActivityError] = useState('');
  const [tableInfo, setTableInfo] = useState([]);
  const [tableInfoLoading, setTableInfoLoading] = useState(false);
  const [tableInfoError, setTableInfoError] = useState('');
  const [tableInfoFilter, setTableInfoFilter] = useState('all');
  const [syncAudit, setSyncAudit] = useState([]);
  const [syncAuditLoading, setSyncAuditLoading] = useState(false);
  const [syncAuditError, setSyncAuditError] = useState('');
  const [syncAuditStatusFilter, setSyncAuditStatusFilter] = useState('all');
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState('');
  const [operationsStatusFilter, setOperationsStatusFilter] = useState('all');
  const [operationTriggering, setOperationTriggering] = useState('');
  const [catchupSource, setCatchupSource] = useState('all');
  const [catchupStartDate, setCatchupStartDate] = useState('');
  const [catchupEndDate, setCatchupEndDate] = useState('');
  const [catchupDays, setCatchupDays] = useState('1');
  const [catchupChunkDays, setCatchupChunkDays] = useState('1');

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
  const canViewTableInfo = Boolean(isAdmin || profile?.role === 'etl_admin');

  const toSafeId = (name) => {
    const s = (name || '').trim().toLowerCase();
    const cleaned = s
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return cleaned || 'source';
  };

  const authLabel = (auth) => {
    if (auth === 'bearer') return 'Bearer Token';
    if (auth === 'api_key') return 'API Key';
    if (auth === 'basic') return 'Username/Password';
    if (auth === 'oauth2') return 'OAuth 2.0';
    return auth || '—';
  };

  const normalizeAuthType = (auth) => {
    return auth || 'api_key';
  };

  const syncIntervalLabel = (seconds) => {
    const s = Number(seconds);
    if (!Number.isFinite(s)) return '—';
    if (s === 0) return 'One-time';
    if (s === 300) return '5 minutes';
    if (s === 600) return '10 minutes';
    if (s === 1800) return '30 minutes';
    if (s === 3600) return '1 hour';
    if (s === 86400) return 'Daily';
    if (s === 2592000) return 'Monthly';
    if (s < 3600) return `${Math.round(s / 60)} minutes`;
    if (s > 0 && s < 86400) return `${Math.round(s / 3600)} hours`;
    if (s >= 86400 && s < 2592000) return `${Math.round(s / 86400)} days`;
    return `${Math.round(s / 3600)} hours`;
  };

  const timeAgo = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    // Always display in New York time (EST/EDT)
    return d.toLocaleString('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    });
  };

  const isTableIssue = (table) => !table?.exists || Number(table?.row_count || 0) === 0;

  const filteredTableInfo = useMemo(() => {
    if (tableInfoFilter !== 'issues') return tableInfo;
    return tableInfo.filter(isTableIssue);
  }, [tableInfo, tableInfoFilter]);

  const filteredSyncAudit = useMemo(() => {
    if (syncAuditStatusFilter === 'all') return syncAudit;
    const wanted = String(syncAuditStatusFilter || '').toUpperCase();
    return syncAudit.filter((row) => String(row?.status || '').toUpperCase() === wanted);
  }, [syncAudit, syncAuditStatusFilter]);

  const filteredOperations = useMemo(() => {
    if (operationsStatusFilter === 'all') return operations;
    const wanted = String(operationsStatusFilter || '').toUpperCase();
    return operations.filter((row) => String(row?.status || '').toUpperCase() === wanted);
  }, [operations, operationsStatusFilter]);

  const syncAuditStatusParam = syncAuditStatusFilter === 'all' ? '' : syncAuditStatusFilter;
  const operationsStatusParam = operationsStatusFilter === 'all' ? '' : operationsStatusFilter;

  const iconFor = (sourceId) => {
    const id = (sourceId || '').toLowerCase();
    if (id === 'samsara') return { icon: 'pin', iconClass: 'samsara' };
    if (id === 'routegenie') return { icon: 'map', iconClass: 'routegenie' };
    return { icon: 'link', iconClass: 'default' };
  };

  const loadSources = async () => {
    if (!user) return;
    setSourcesLoading(true);
    setSourcesError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load sources (${res.status})`);
      }
      setSources(Array.isArray(data?.sources) ? data.sources : []);
    } catch (e) {
      setSources([]);
      setSourcesError(e?.message || 'Failed to load sources');
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadActivity = async () => {
    if (!user) return;
    setActivityError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources/activity?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load activity (${res.status})`);
      }
      setActivity(Array.isArray(data?.activity) ? data.activity : []);
    } catch (e) {
      setActivity([]);
      setActivityError(e?.message || 'Failed to load activity');
    }
  };

  const loadTableInfo = async () => {
    if (!user || !canViewTableInfo) {
      setTableInfo([]);
      setTableInfoError('');
      return;
    }

    setTableInfoLoading(true);
    setTableInfoError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources/table-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load table information (${res.status})`);
      }
      setTableInfo(Array.isArray(data?.tables) ? data.tables : []);
    } catch (e) {
      setTableInfo([]);
      setTableInfoError(e?.message || 'Failed to load table information');
    } finally {
      setTableInfoLoading(false);
    }
  };

  const loadSyncAudit = async () => {
    if (!user || !canViewTableInfo) {
      setSyncAudit([]);
      setSyncAuditError('');
      return;
    }

    setSyncAuditLoading(true);
    setSyncAuditError('');
    try {
      const token = await getIdToken();
      const params = new URLSearchParams({ limit: '20' });
      if (syncAuditStatusParam) params.set('status', syncAuditStatusParam);
      const res = await fetch(`${apiBaseUrl}/api/sources/sync-audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load sync audit (${res.status})`);
      }
      setSyncAudit(Array.isArray(data?.audit) ? data.audit : []);
    } catch (e) {
      setSyncAudit([]);
      setSyncAuditError(e?.message || 'Failed to load sync audit');
    } finally {
      setSyncAuditLoading(false);
    }
  };

  const loadOperations = async () => {
    if (!user || !canViewTableInfo) {
      setOperations([]);
      setOperationsError('');
      return;
    }

    setOperationsLoading(true);
    setOperationsError('');
    try {
      const token = await getIdToken();
      const params = new URLSearchParams({ limit: '20' });
      if (operationsStatusParam) params.set('status', operationsStatusParam);
      const res = await fetch(`${apiBaseUrl}/api/sources/operations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load operations (${res.status})`);
      }
      setOperations(Array.isArray(data?.operations) ? data.operations : []);
    } catch (e) {
      setOperations([]);
      setOperationsError(e?.message || 'Failed to load operations');
    } finally {
      setOperationsLoading(false);
    }
  };

  const triggerOperation = async (operationName) => {
    if (!user || !canViewTableInfo) return;
    setOperationTriggering(operationName);
    setOperationsError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources/operations/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operation_name: operationName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to trigger operation (${res.status})`);
      }
      await Promise.all([loadOperations(), loadSyncAudit(), loadTableInfo()]);
    } catch (e) {
      setOperationsError(e?.message || 'Failed to trigger operation');
    } finally {
      setOperationTriggering('');
    }
  };

  const triggerCustomCatchup = async () => {
    if (!user || !canViewTableInfo) return;
    setOperationTriggering('source_sync_custom');
    setOperationsError('');
    try {
      const token = await getIdToken();
      const payload = {
        operation_name: 'source_sync_custom',
        source: catchupSource,
        chunk_days: Number(catchupChunkDays || 1),
      };

      if (catchupStartDate && catchupEndDate) {
        payload.start_date = catchupStartDate;
        payload.end_date = catchupEndDate;
      } else {
        payload.days = Number(catchupDays || 1);
      }

      const res = await fetch(`${apiBaseUrl}/api/sources/operations/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to trigger catch-up (${res.status})`);
      }
      await Promise.all([loadOperations(), loadSyncAudit(), loadTableInfo()]);
    } catch (e) {
      setOperationsError(e?.message || 'Failed to trigger catch-up');
    } finally {
      setOperationTriggering('');
    }
  };

  const refreshEtlAdmin = async () => {
    await Promise.all([loadTableInfo(), loadSyncAudit(), loadOperations()]);
  };

  useEffect(() => {
    loadSources();
    loadActivity();
    loadTableInfo();
    loadSyncAudit();
    loadOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, canViewTableInfo, syncAuditStatusParam, operationsStatusParam]);

  const openAddModal = () => {
    if (!canManageSources) return;
    setEditing(null);
    setAuthType('api_key');
    setBaselineAuthType('api_key');
    setSaveError('');
    setTestResult(null);
    setEditingHasCredentials(false);
    const initialRows = defaultAuthPropertiesFor('api_key');
    setAuthProperties(initialRows);
    setBaselineCredentialState(rowsToCredentialState(initialRows));
    setQueryParamRows([{ key: '', value: '' }]);
    setForm({
      sourceName: '',
      sourceDesc: '',
      sourceType: 'api',
      baseUrl: '',
      endpoint: '',
      syncInterval: '300',
    });
    setModalOpen(true);
  };

  const openEditModal = async (sourceId) => {
    if (!canManageSources) return;
    const src = sources.find((s) => s.id === sourceId);
    setEditing(sourceId);
    setSaveError('');
    setTestResult(null);
    const initialAuth = normalizeAuthType(src?.auth_type);
    setAuthType(initialAuth);
    setBaselineAuthType(initialAuth);
    setEditingHasCredentials(Boolean(src?.has_credentials));
    const initialRows = defaultAuthPropertiesFor(initialAuth);
    setAuthProperties(initialRows);
    setBaselineCredentialState(rowsToCredentialState(initialRows));
    setQueryParamRows(queryParamsToRows(src?.query_params));
    setForm((prev) => ({
      ...prev,
      sourceName: src?.name || '',
      sourceDesc: src?.description || '',
      sourceType: src?.source_type || 'api',
      baseUrl: src?.base_url || '',
      endpoint: src?.endpoint || '',
      syncInterval: String(src?.sync_interval ?? 300),
    }));
    setModalOpen(true);

    if (!src?.has_credentials) return;

    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources/${encodeURIComponent(sourceId)}/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Failed to load credentials (${res.status})`);
      }

      const creds = data?.credentials || null;
      if (!creds) return;

      const normalized = normalizeLoadedCredentials(creds, src?.auth_type);
      const nextAuth = normalizeAuthType(normalized?.auth_type || src?.auth_type);
      setAuthType(nextAuth);
      setBaselineAuthType(nextAuth);
      setBaselineCredentialState({ properties: normalized.properties || {}, secrets: normalized.secrets || [] });
      setAuthProperties(
        credentialStateToRows({ properties: normalized.properties || {}, secrets: normalized.secrets || [] })
      );
    } catch (e) {
      setSaveError(e?.message || 'Failed to load credentials');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaveError('');
    setTestResult(null);
  };

  const buildCurrentPayload = () => {
    const base_url = form.baseUrl.trim();
    const endpoint = form.endpoint.trim();
    const query_params = rowsToQueryParams(queryParamRows);
    const payload = Object.keys(query_params).length
      ? { base_url, endpoint, auth_type: authType, query_params }
      : { base_url, endpoint, auth_type: authType };

    if (editing) {
      if (!editingHasCredentials) {
        // No stored credentials yet; test/save must use an explicit credentials payload.
        const credentials = buildCreateCredentials();
        return { ...payload, credentials };
      }

      const delta = buildCredentialDelta();
      if (!delta) {
        // Test/save using stored credentials
        return { ...payload, source_id: editing };
      }
      return { ...payload, source_id: editing, credentials: delta };
    }

    const credentials = buildCreateCredentials();
    return { ...payload, credentials };
  };

  const payloadsMatch = (p1, p2) => JSON.stringify(p1) === JSON.stringify(p2);
  const testPassed = lastTestedPayload && payloadsMatch(lastTestedPayload, buildCurrentPayload());

  const testConnection = async () => {
    if (!canManageSources) return;
    setSaveError('');
    setTestResult(null);

    const payload = buildCurrentPayload();
    if (!payload.base_url) {
      setSaveError('Base URL is required before testing.');
      return;
    }
    if (!payload.endpoint) {
      setSaveError('Endpoint Path is required before testing.');
      return;
    }
    if (/^https?:\/\//i.test(payload.endpoint)) {
      setSaveError('Endpoint Path must be relative (not a full URL). Use Base URL for the host.');
      return;
    }

    // Validate only when we are not relying on stored credentials.
    if (!editing || !editingHasCredentials) {
      const creds = payload.credentials || buildCreateCredentials();
      const missing = validateCredentialsForTestOrCreate(creds);
      if (missing.length > 0) {
        setSaveError(`${missing.join(', ')} required.`);
        return;
      }
    }

    setTestingConnection(true);
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/sources/fetch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
        throw new Error(data?.detail || `Test failed (${res.status})`);
      }

      const ok = Boolean(data?.ok);
      const preview = data?.preview_json != null ? data.preview_json : null;
      const previewText = data?.preview_text != null ? String(data.preview_text) : null;
      setTestResult({
        ok,
        message: ok ? 'Fetch successful' : 'Fetch failed',
        statusCode: data?.status_code,
        responseTimeMs: data?.response_time_ms,
        preview,
        previewText,
      });
      if (ok) setLastTestedPayload(payload);
    } catch (e) {
      setTestResult({ ok: false, message: e?.message || 'Test failed' });
    } finally {
      setTestingConnection(false);
    }
  };

  const toggleSource = (sourceId) => {
    if (!canManageSources) {
      setSourcesError(ACCESS_DENIED_MESSAGE);
      return;
    }
    setSourcesError('');
    const current = sources.find((s) => s.id === sourceId);
    if (!current) return;

    const nextActive = !current.is_active;
    // Optimistic UI
    setSources((prev) => prev.map((s) => (s.id === sourceId ? { ...s, is_active: nextActive } : s)));
    (async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(`${apiBaseUrl}/api/sources/${encodeURIComponent(sourceId)}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_active: nextActive }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
          throw new Error(data?.detail || `Failed to update source (${res.status})`);
        }
        loadActivity();
      } catch (e) {
        // Revert on error
        setSources((prev) => prev.map((s) => (s.id === sourceId ? { ...s, is_active: !nextActive } : s)));
        setSourcesError(e?.message || 'Failed to update source');
      }
    })();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  const saveSource = async () => {
    if (!canManageSources) {
      setSaveError(ACCESS_DENIED_MESSAGE);
      return;
    }
    setSaveError('');

    const name = form.sourceName.trim();
    const baseUrl = form.baseUrl.trim();
    const endpoint = form.endpoint.trim();
    const sourceType = (form.sourceType || 'api').trim();
    if (!name) {
      setSaveError('Source Name is required.');
      return;
    }
    if (!baseUrl) {
      setSaveError('Base URL is required.');
      return;
    }
    if (!endpoint) {
      setSaveError('Endpoint Path is required.');
      return;
    }
    if (!/^https?:\/\//i.test(baseUrl)) {
      setSaveError('Base URL must start with http:// or https://');
      return;
    }
    if (/^https?:\/\//i.test(endpoint)) {
      setSaveError('Endpoint Path must be relative (not a full URL).');
      return;
    }

    const sourceId = editing || toSafeId(name);
    const parsedSyncInterval = Number.parseInt(String(form.syncInterval), 10);
    const syncInterval = Number.isFinite(parsedSyncInterval) ? parsedSyncInterval : 3600;

    const creating = !editing;
    let credentials = null;
    if (creating) {
      const createCreds = buildCreateCredentials();
      const missing = validateCredentialsForTestOrCreate(createCreds);
      if (missing.length > 0) {
        setSaveError(`${missing.join(', ')} required.`);
        return;
      }
      credentials = createCreds;
    } else {
      // When switching auth types on an existing source, we must require full
      // credentials for the new auth type (there is nothing to "keep").
      const authChanged = normalizeAuthType(authType) !== normalizeAuthType(baselineAuthType);
      if (authChanged) {
        const fullCreds = buildCreateCredentials();
        const missing = validateCredentialsForTestOrCreate(fullCreds);
        if (missing.length > 0) {
          setSaveError(`${missing.join(', ')} required.`);
          return;
        }
      }
      credentials = buildCredentialDelta();
    }

    try {
      const token = await getIdToken();
      const query_params = rowsToQueryParams(queryParamRows);

      if (editing) {
        const updatePayload = {
          name,
          description: form.sourceDesc.trim() || null,
          source_type: sourceType,
          base_url: baseUrl,
          endpoint,
          auth_type: authType,
          sync_interval: syncInterval,
        };
        if (Object.keys(query_params).length) updatePayload.query_params = query_params;
        if (credentials) updatePayload.credentials = credentials;

        const res = await fetch(`${apiBaseUrl}/api/sources/${encodeURIComponent(sourceId)}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
          throw new Error(data?.detail || `Failed to update source (${res.status})`);
        }
      } else {
        const createPayload = {
          id: sourceId,
          name,
          description: form.sourceDesc.trim() || null,
          source_type: sourceType,
          base_url: baseUrl,
          endpoint,
          auth_type: authType,
          sync_interval: syncInterval,
          is_active: true,
        };
        if (Object.keys(query_params).length) createPayload.query_params = query_params;
        if (credentials) createPayload.credentials = credentials;

        const res = await fetch(`${apiBaseUrl}/api/sources`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createPayload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 403) throw new Error(ACCESS_DENIED_MESSAGE);
          throw new Error(data?.detail || `Failed to create source (${res.status})`);
        }
      }

      closeModal();
      await loadSources();
      await loadActivity();
    } catch (e) {
      setSaveError(e?.message || 'Failed to save source');
    }
  };

  if (!canManageSources) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Access Denied</h2>
        <p>{ACCESS_DENIED_MESSAGE}</p>
        {user?.email && (
          <p style={{ fontSize: '0.9em', color: '#666' }}>Signed in as: {user.email}</p>
        )}
        <Link to="/kpi-dashboard">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="sol-shell">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="logo">
          <h1>
            <div className="logo-icon"><AppIcon name="truck" /></div>
            <span>SOL Transport</span>
          </h1>
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          <Link className="nav-item" to="/kpi-dashboard">
            <span className="nav-item-icon"><AppIcon name="dashboard" /></span>
            Dashboard
          </Link>

        </div>

        <div className="nav-section">
          <div className="nav-section-title">Administration</div>
          {isAdmin && <Link className="nav-item" to="/user-management">
            <span className="nav-item-icon"><AppIcon name="users" /></span>
            User Management
          </Link>}
          {isAdmin && <Link className="nav-item active" to="/source-config">
            <span className="nav-item-icon"><AppIcon name="link" /></span>
            Source Config
          </Link>}
          {isAdmin && <Link className="nav-item" to="/activity-log">
            <span className="nav-item-icon"><AppIcon name="activity" /></span>
            User Activity Log
          </Link>}
          {canUpload && <Link className="nav-item" to="/upload">
            <span className="nav-item-icon"><AppIcon name="upload" /></span>
            Upload Files
          </Link>}
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{avatar}</div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{isAdmin ? 'Admin' : 'User'}</div>
          </div>
          <button className="logout-btn" type="button" onClick={handleLogout} aria-label="Logout">
            <AppIcon name="login" size={14} />
            Logout
          </button>
        </div>
      </aside>

      <main className="main-content">
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          type="button" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <AppIcon name="menu" size={18} />
        </button>

        <header className="header">
          <div className="header-left">
            <h2 className="title-with-icon"><AppIcon name="link" /> Source Configuration</h2>
            <p>Manage data source connections, credentials, and sync settings</p>
          </div>
          <div className="header-right">
            {canManageSources ? (
              <button className="btn btn-secondary" type="button" onClick={openAddModal}>
                <AppIcon name="plus" /> Add Source
              </button>
            ) : null}
          </div>
        </header>

        {sourcesLoading ? <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><TruckLoader message="Loading sources…" size="sm" /></div> : null}
        {sourcesError ? <div style={{ marginBottom: 12, color: 'var(--danger)' }}>{sourcesError}</div> : null}

        <div className="sources-grid">
          {sources.map((s) => {
            const icon = iconFor(s.id);
            const active = Boolean(s.is_active);
            return (
            <div key={s.id} className={`source-card ${active ? '' : 'inactive'}`}>
              <div className="source-header">
                <div className="source-info">
                  <div className={`source-icon ${icon.iconClass}`}><AppIcon name={icon.icon} /></div>
                  <div>
                    <div className="source-title">{s.name}</div>
                    <div className="source-description">{s.description}</div>
                    <span className={`source-status ${active ? 'active' : 'inactive'}`}>
                      ● {active ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                </div>
                {canManageSources ? (
                  <div
                    className={`source-toggle ${active ? 'active' : ''}`}
                    role="switch"
                    aria-checked={active}
                    tabIndex={0}
                    onClick={() => toggleSource(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleSource(s.id);
                    }}
                  ></div>
                ) : null}
              </div>

              <div className="source-details">
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="tag" /> Type</span>
                  <span className="detail-value">{(s.source_type || 'api').replace('_', ' ')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="link" /> Base URL</span>
                  <span className="detail-value">{s.base_url || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="endpoint" /> Endpoint Path</span>
                  <span className="detail-value">{s.endpoint}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="lock" /> Auth Type</span>
                  <span className="detail-value">{authLabel(s.auth_type)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="key" /> Credential</span>
                  <span className="detail-value masked">{s.has_credentials ? '•••••••• (stored in Secret Manager)' : 'Not set'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><AppIcon name="sync" /> Sync Interval</span>
                  <span className="detail-value">{syncIntervalLabel(s.sync_interval)}</span>
                </div>
              </div>

              <div className="sync-info">
                <div className="sync-stat">
                  <div className="sync-stat-value">—</div>
                  <div className="sync-stat-label">Records Today</div>
                </div>
                <div className="sync-stat">
                  <div className="sync-stat-value">{s.last_sync ? timeAgo(s.last_sync) : '—'}</div>
                  <div className="sync-stat-label">Last Sync</div>
                </div>
                <div className="sync-stat">
                  <div className="sync-stat-value">{s.error_message ? 'error' : 'ok'}</div>
                  <div className="sync-stat-label">Uptime</div>
                </div>
              </div>

              {s.error_message ? (
                <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 13 }}>{s.error_message}</div>
              ) : null}

              {canManageSources ? (
                <div className="source-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => openEditModal(s.id)}>
                    <AppIcon name="pencil" /> Edit
                  </button>
                  <button className="btn btn-secondary" type="button">
                    <AppIcon name="refresh" /> Sync Now
                  </button>
                </div>
              ) : null}
            </div>
          );
          })}

          {canManageSources ? (
            <div
              className="add-source-card"
              role="button"
              tabIndex={0}
              onClick={openAddModal}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openAddModal();
              }}
            >
              <div className="add-source-icon"><AppIcon name="plus" /></div>
              <div className="add-source-title">Add New Source</div>
              <div className="add-source-desc">Connect another data provider</div>
            </div>
          ) : null}
        </div>

        <div className="activity-section">
          <div className="activity-header">
            <div className="activity-title title-with-icon"><AppIcon name="activity" /> Recent Activity</div>
            <button className="btn btn-secondary" type="button">
              View All
            </button>
          </div>
          {activityError ? <div style={{ marginBottom: 10, color: 'var(--danger)' }}>{activityError}</div> : null}
          <div className="activity-list">
            {activity.length === 0 ? (
              <div style={{ padding: 14, opacity: 0.75 }}>No recent activity yet.</div>
            ) : (
              activity.map((a) => {
                const icon =
                  a.action === 'created'
                    ? 'plus'
                    : a.action === 'updated'
                      ? 'pencil'
                      : a.action === 'enabled'
                        ? 'check'
                        : a.action === 'disabled'
                          ? 'ban'
                          : 'refresh';
                const iconClass = a.action === 'created' || a.action === 'updated' || a.action === 'enabled' || a.action === 'disabled' ? 'config' : 'sync';
                return (
                  <div key={a.id} className="activity-item">
                    <div className={`activity-icon ${iconClass}`}><AppIcon name={icon} /></div>
                    <div className="activity-content">
                      <div className="activity-message">{a.message}</div>
                      <div className="activity-time">{timeAgo(a.created_at)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {canViewTableInfo ? (
          <div className="table-card" style={{ marginTop: 16 }}>
            <div className="table-header">
              <div className="table-title title-with-icon"><AppIcon name="database" /> ETL Admin: Audit & Recovery</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  className="filter-select"
                  value={tableInfoFilter}
                  onChange={(e) => setTableInfoFilter(e.target.value)}
                  style={{ minWidth: 170 }}
                >
                  <option value="all">All Tables</option>
                  <option value="issues">Issues Only</option>
                </select>
                <select
                  className="filter-select"
                  value={syncAuditStatusFilter}
                  onChange={(e) => setSyncAuditStatusFilter(e.target.value)}
                  style={{ minWidth: 150 }}
                >
                  <option value="all">All Sync Runs</option>
                  <option value="SUCCESS">Sync Success</option>
                  <option value="FAILED">Sync Failed</option>
                </select>
                <select
                  className="filter-select"
                  value={operationsStatusFilter}
                  onChange={(e) => setOperationsStatusFilter(e.target.value)}
                  style={{ minWidth: 160 }}
                >
                  <option value="all">All Operations</option>
                  <option value="RUNNING">Running</option>
                  <option value="SUCCESS">Success</option>
                  <option value="FAILED">Failed</option>
                </select>
                <button className="btn btn-secondary" type="button" onClick={refreshEtlAdmin}>
                  Refresh
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => triggerOperation('source_sync_full_1d')}
                disabled={operationTriggering !== ''}
              >
                {operationTriggering === 'source_sync_full_1d' ? 'Running…' : 'Run Full Sync (1d)'}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => triggerOperation('source_sync_routegenie_7d')}
                disabled={operationTriggering !== ''}
              >
                {operationTriggering === 'source_sync_routegenie_7d' ? 'Running…' : 'RouteGenie Recovery (7d)'}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => triggerOperation('source_sync_samsara_7d')}
                disabled={operationTriggering !== ''}
              >
                {operationTriggering === 'source_sync_samsara_7d' ? 'Running…' : 'Samsara Recovery (7d)'}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => triggerOperation('create_safety_scores_table')}
                disabled={operationTriggering !== ''}
              >
                {operationTriggering === 'create_safety_scores_table' ? 'Running…' : 'Create Safety Table'}
              </button>
            </div>

            <div style={{ marginBottom: 12, padding: 12, border: '1px solid var(--border-color)', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Catch-up Recovery (missing/corrupt days)</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select className="filter-select" value={catchupSource} onChange={(e) => setCatchupSource(e.target.value)}>
                  <option value="all">All Sources</option>
                  <option value="routegenie">RouteGenie only</option>
                  <option value="samsara">Samsara only</option>
                </select>
                <input
                  type="date"
                  className="form-input"
                  value={catchupStartDate}
                  onChange={(e) => setCatchupStartDate(e.target.value)}
                  style={{ maxWidth: 180 }}
                />
                <span>to</span>
                <input
                  type="date"
                  className="form-input"
                  value={catchupEndDate}
                  onChange={(e) => setCatchupEndDate(e.target.value)}
                  style={{ maxWidth: 180 }}
                />
                <input
                  type="number"
                  min="1"
                  max="365"
                  className="form-input"
                  value={catchupDays}
                  onChange={(e) => setCatchupDays(e.target.value)}
                  style={{ maxWidth: 110 }}
                  placeholder="days"
                  title="Used when start/end dates are blank"
                />
                <input
                  type="number"
                  min="1"
                  max="7"
                  className="form-input"
                  value={catchupChunkDays}
                  onChange={(e) => setCatchupChunkDays(e.target.value)}
                  style={{ maxWidth: 130 }}
                  placeholder="chunk"
                  title="Chunk days per API window"
                />
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={triggerCustomCatchup}
                  disabled={operationTriggering !== ''}
                >
                  {operationTriggering === 'source_sync_custom' ? 'Running…' : 'Run Catch-up'}
                </button>
              </div>
              <div style={{ marginTop: 6, opacity: 0.75 }}>
                Tip: pick a specific start/end date for known gaps, or leave dates blank and use days=1 for yesterday catch-up.
              </div>
            </div>

            {tableInfoError ? (
              <div style={{ marginBottom: 10, color: 'var(--danger)' }}>{tableInfoError}</div>
            ) : null}
            {operationsError ? (
              <div style={{ marginBottom: 10, color: 'var(--danger)' }}>{operationsError}</div>
            ) : null}
            {syncAuditError ? (
              <div style={{ marginBottom: 10, color: 'var(--danger)' }}>{syncAuditError}</div>
            ) : null}

            {tableInfoLoading ? (
              <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'center' }}><TruckLoader message="Loading table information…" size="sm" /></div>
            ) : filteredTableInfo.length === 0 ? (
              <div style={{ padding: '12px 0', opacity: 0.75 }}>
                {tableInfoFilter === 'issues' ? 'No tables with issues.' : 'No table information available.'}
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Table</th>
                    <th>Rows</th>
                    <th>Data Range</th>
                    <th>Last Synced</th>
                    <th>Size (MB)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTableInfo.map((t) => {
                    const range = t.min_time || t.max_time ? `${formatDateTime(t.min_time)} → ${formatDateTime(t.max_time)}` : '—';
                    return (
                      <tr key={t.table_name}>
                        <td>{t.table_name}</td>
                        <td>{Number(t.row_count || 0).toLocaleString()}</td>
                        <td>{range}</td>
                        <td>{formatDateTime(t.last_synced_at)}</td>
                        <td>{Number(t.size_mb || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <div style={{ marginTop: 16, fontWeight: 700 }}>Recent Sync Audit</div>
            {syncAuditLoading ? (
              <div style={{ padding: '8px 0', display: 'flex', justifyContent: 'center' }}><TruckLoader message="Loading sync audit…" size="sm" /></div>
            ) : filteredSyncAudit.length === 0 ? (
              <div style={{ padding: '8px 0', opacity: 0.75 }}>No sync audit records.</div>
            ) : (
              <table className="data-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration (ms)</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSyncAudit.map((row) => (
                    <tr key={row.id}>
                      <td>{row.job_name}</td>
                      <td>{row.status}</td>
                      <td>{formatDateTime(row.started_at)}</td>
                      <td>{row.duration_ms ?? '—'}</td>
                      <td>{row.error_message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ marginTop: 16, fontWeight: 700 }}>Recent Operations</div>
            {operationsLoading ? (
              <div style={{ padding: '8px 0', display: 'flex', justifyContent: 'center' }}><TruckLoader message="Loading operations…" size="sm" /></div>
            ) : filteredOperations.length === 0 ? (
              <div style={{ padding: '8px 0', opacity: 0.75 }}>No operation records.</div>
            ) : (
              <table className="data-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Operation</th>
                    <th>Status</th>
                    <th>Requested By</th>
                    <th>Started</th>
                    <th>Duration (ms)</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOperations.map((op) => (
                    <tr key={op.operation_id}>
                      <td>{op.operation_name}</td>
                      <td>{op.status}</td>
                      <td>{op.requested_by_email || '—'}</td>
                      <td>{formatDateTime(op.started_at)}</td>
                      <td>{op.duration_ms ?? '—'}</td>
                      <td>{op.error_message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : null}
      </main>

      <div className={`modal-overlay ${modalOpen ? 'active' : ''}`} role="dialog" aria-modal="true">
        <div className="modal">
          <div className="modal-header">
            <div className="modal-title">
                  {editing
                    ? `Edit ${sources.find((s) => s.id === editing)?.name || editing}`
                    : 'Add New Source'}
            </div>
            <button className="modal-close" type="button" onClick={closeModal} aria-label="Close">
              ✕
            </button>
          </div>
          <div className="modal-body">
            {saveError ? (
              <div className="test-result err" style={{ marginBottom: 12 }}>
                {saveError}
              </div>
            ) : null}
            {testResult ? (
              <div className={`test-result ${testResult.ok ? 'ok' : 'err'}`} style={{ marginBottom: 12 }}>
                {testResult.message}
                {typeof testResult.statusCode === 'number' ? ` (HTTP ${testResult.statusCode})` : ''}
                {typeof testResult.responseTimeMs === 'number' ? ` • ${testResult.responseTimeMs}ms` : ''}
              </div>
            ) : null}
            {testResult && (testResult?.preview != null || testResult?.previewText) ? (
              <pre
                style={{
                  marginBottom: 12,
                  maxHeight: 220,
                  overflow: 'auto',
                  padding: 12,
                  fontSize: 12,
                }}
              >
                {testResult?.preview != null
                  ? JSON.stringify(testResult.preview, null, 2).slice(0, 20000)
                  : String(testResult.previewText).slice(0, 20000)}
              </pre>
            ) : null}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Source Name <span>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Fleet Management API"
                  value={form.sourceName}
                  onChange={(e) => setForm((p) => ({ ...p, sourceName: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Source Type <span>*</span>
                </label>
                <select
                  className="form-select"
                  value={form.sourceType}
                  onChange={(e) => setForm((p) => ({ ...p, sourceType: e.target.value }))}
                >
                  <option value="api">API</option>
                  <option value="sharepoint">SharePoint</option>
                  <option value="database">Database</option>
                  <option value="bucket_url">Bucket URL</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-input"
                placeholder="Brief description of this data source"
                value={form.sourceDesc}
                onChange={(e) => setForm((p) => ({ ...p, sourceDesc: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Base URL <span>*</span>
              </label>
              <input
                type="text"
                className="form-input code"
                placeholder="https://api.example.com"
                value={form.baseUrl}
                onChange={(e) => {
                  setLastTestedPayload(null);
                  setForm((p) => ({ ...p, baseUrl: e.target.value }));
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Endpoint Path <span>*</span>
              </label>
              <input
                type="text"
                className="form-input code"
                placeholder="/v1/resource"
                value={form.endpoint}
                onChange={(e) => {
                  setLastTestedPayload(null);
                  setForm((p) => ({ ...p, endpoint: e.target.value }));
                }}
              />
              <div className="form-help" style={{ marginTop: 6 }}>
                Use a relative path here (not a full URL).
              </div>
            </div>

            <div className="credential-section" style={{ marginTop: 14 }}>
              <div className="credential-header">
                <span className="credential-header-icon"><AppIcon name="search" /></span>
                <span className="credential-header-title">Query Parameters</span>
              </div>

              <div className="form-help" style={{ marginBottom: 10 }}>
                Some APIs require query parameters (for example, time ranges like startMs).
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(queryParamRows.length ? queryParamRows : [{ key: '', value: '' }]).map((row, idx) => (
                  <div key={idx} className="form-row" style={{ alignItems: 'center' }}>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type="text"
                        className="form-input code"
                        placeholder="key (e.g., startMs)"
                        value={row?.key || ''}
                        onChange={(e) => {
                          setLastTestedPayload(null);
                          setQueryParamRows((prev) => {
                            const next = [...(prev.length ? prev : [{ key: '', value: '' }])];
                            next[idx] = { ...next[idx], key: e.target.value };
                            return next;
                          });
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type="text"
                        className="form-input code"
                        placeholder="value"
                        value={row?.value || ''}
                        onChange={(e) => {
                          setLastTestedPayload(null);
                          setQueryParamRows((prev) => {
                            const next = [...(prev.length ? prev : [{ key: '', value: '' }])];
                            next[idx] = { ...next[idx], value: e.target.value };
                            return next;
                          });
                        }}
                      />
                    </div>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => {
                        setLastTestedPayload(null);
                        setQueryParamRows((prev) => {
                          const next = [...(prev.length ? prev : [{ key: '', value: '' }])];
                          next.splice(idx, 1);
                          return next.length ? next : [{ key: '', value: '' }];
                        });
                      }}
                      disabled={(queryParamRows.length ? queryParamRows : [{ key: '', value: '' }]).length <= 1}
                      aria-label="Remove query param row"
                    >
                      <AppIcon name="ban" />
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 8 }}>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => {
                    setLastTestedPayload(null);
                    setQueryParamRows((prev) => [...(prev.length ? prev : [{ key: '', value: '' }]), { key: '', value: '' }]);
                  }}
                >
                  <AppIcon name="plus" /> Add Query Param
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Authentication Type <span>*</span>
                </label>
                <select
                  className="form-select"
                  value={authType}
                  onChange={(e) => {
                    const nextAuth = e.target.value;
                    setLastTestedPayload(null);
                    setAuthType(nextAuth);
                    const nextRows = defaultAuthPropertiesFor(nextAuth);
                    setAuthProperties(nextRows);
                    // For new sources, baseline == current. For edits, keep the
                    // baseline from the stored credentials so deltas can remove
                    // old keys when switching auth types.
                    if (!editing) {
                      setBaselineAuthType(nextAuth);
                      setBaselineCredentialState(rowsToCredentialState(nextRows));
                    }
                  }}
                >
                  <option value="api_key">API Key</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Username/Password</option>
                  <option value="oauth2">OAuth 2.0 (Client Credentials)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sync Interval</label>
                <select
                  className="form-select"
                  value={form.syncInterval}
                  onChange={(e) => setForm((p) => ({ ...p, syncInterval: e.target.value }))}
                >
                  <option value="0">One-time</option>
                  <option value="300">Every 5 minutes</option>
                  <option value="600">Every 10 minutes</option>
                  <option value="1800">Every 30 minutes</option>
                  <option value="3600">Every hour</option>
                  <option value="86400">Daily</option>
                  <option value="2592000">Monthly</option>
                </select>
              </div>
            </div>

            <div className="credential-section">
              <div className="credential-header">
                <span className="credential-header-icon"><AppIcon name="lock" /></span>
                <span className="credential-header-title">Credentials (Stored Securely)</span>
              </div>

              <div className="form-help" style={{ marginBottom: 10 }}>
                Secret values are never displayed. Leave secret values blank to keep stored secrets unchanged.
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(authProperties.length ? authProperties : [{ key: '', value: '', secret: false }]).map((row, idx) => (
                  <div key={idx} className="form-row" style={{ alignItems: 'center' }}>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type="text"
                        className="form-input code"
                        placeholder="key"
                        value={row?.key || ''}
                        onChange={(e) => {
                          setLastTestedPayload(null);
                          setAuthProperties((prev) => {
                            const next = [...(prev.length ? prev : [{ key: '', value: '', secret: false }])];
                            next[idx] = { ...next[idx], key: e.target.value };
                            return next;
                          });
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                      <input
                        type={row?.secret ? 'password' : 'text'}
                        className="form-input code"
                        placeholder={row?.secret ? 'secret value (leave blank to keep)' : 'value'}
                        value={row?.value || ''}
                        onChange={(e) => {
                          setLastTestedPayload(null);
                          setAuthProperties((prev) => {
                            const next = [...(prev.length ? prev : [{ key: '', value: '', secret: false }])];
                            next[idx] = { ...next[idx], value: e.target.value };
                            return next;
                          });
                        }}
                      />
                    </div>
                    <label className="form-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(row?.secret)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLastTestedPayload(null);
                          setAuthProperties((prev) => {
                            const next = [...(prev.length ? prev : [{ key: '', value: '', secret: false }])];
                            next[idx] = { ...next[idx], secret: checked };
                            return next;
                          });
                        }}
                      />
                      <span>Secret</span>
                    </label>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => {
                        setLastTestedPayload(null);
                        setAuthProperties((prev) => {
                          const next = [...(prev.length ? prev : [{ key: '', value: '', secret: false }])];
                          next.splice(idx, 1);
                          return next.length ? next : [{ key: '', value: '', secret: false }];
                        });
                      }}
                      disabled={(authProperties.length ? authProperties : [{ key: '', value: '', secret: false }]).length <= 1}
                      aria-label="Remove auth property row"
                    >
                      <AppIcon name="ban" />
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 8 }}>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => {
                    setLastTestedPayload(null);
                    setAuthProperties((prev) => [...(prev.length ? prev : [{ key: '', value: '', secret: false }]), { key: '', value: '', secret: false }]);
                  }}
                >
                  <AppIcon name="plus" /> Add Field
                </button>
              </div>
            </div>

          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" type="button" onClick={closeModal}>
              Cancel
            </button>
            <button className="btn btn-secondary" type="button" onClick={testConnection} disabled={testingConnection}>
              {testingConnection ? 'Testing…' : <><AppIcon name="search" /> Test Connection</>}
            </button>
            <button className="btn btn-primary" type="button" onClick={saveSource} disabled={!testPassed}>
              <AppIcon name="save" /> Save Source {testPassed ? '✓' : '(test first)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceConfig;
